import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import momentRange from 'moment-range';
import moment from 'moment-timezone';

import { Field, FieldKinds, Template } from '@site-mate/dashpivot-shared-library';

import { FrequencyChartViewVariants } from 'app/analytics/charts/frequency-chart/frequency-chart-view-variants';
import { TemplatesService } from 'app/apps/templates.service';
import { ChartBreakdownStaticOptions } from 'app/shared/model/charts/chart-breakdown-static-options.model';
import { ChartDataIntervalOptions } from 'app/shared/model/charts/chart-data-interval-options.model';
import { ChartDataIntervals } from 'app/shared/model/charts/chart-data-intervals.enum';
import { ChartSettings } from 'app/shared/model/charts/chart-settings.model';
import { ChartUtilsService } from 'app/shared/service/chart-utils.service';
import { DateRangeService } from 'app/shared/service/date-range.service';

// The require()-based block is for browsers, the import-based is for tests execution
const { extendMoment } = typeof require === 'function' ? require('moment-range') : momentRange;

const { range } = extendMoment(moment as any);

@Component({
  selector: 'cc-frequency-chart-settings',
  templateUrl: './frequency-chart-settings.component.html',
  styleUrls: ['./frequency-chart-settings.component.scss'],
})
export class FrequencyChartSettingsComponent implements OnInit, OnChanges {
  @Input() chart: ChartSettings;
  @Input() teamId: string;
  @Input() templates: Template[] = [];
  @Input() dataDateRange: momentRange.DateRange;

  @Output() onChange = new EventEmitter();

  private readonly customTimeframe = { label: 'Custom', value: 'custom' };
  // source
  template: Template;
  fields: Field[] = [];
  field: Field;

  // data
  dateUnits = [];
  timeFrames = [];
  timeFrame;

  // visualize
  showAsOptions = FrequencyChartViewVariants;

  breakdownOptions = [];

  isChartSetup: boolean;

  constructor(
    private readonly templatesService: TemplatesService,
    private readonly chartUtilsService: ChartUtilsService,
    private readonly dateRangeService: DateRangeService,
  ) {}

  ngOnInit() {
    this.timeFrames = this.dateRangeService.getRanges();
    this.timeFrame = this.timeFrames.find((tf) => this.chart.timeframe === tf.value) || this.customTimeframe;
    this.dateUnits = this.getDateUnits(this.timeFrame.range);
    this.isChartSetup = this.chartUtilsService.isChartSetup(this.chart);

    this.onTemplateChange();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.dataDateRange?.currentValue &&
      !changes.dataDateRange.firstChange &&
      ['all', 'custom'].includes(this.chart.timeframe)
    ) {
      this.dateUnits = this.getDateUnits(changes.dataDateRange.currentValue);
      const dateUnit = this.dateUnits.find((unit) => unit.value === this.chart.dateUnit) || this.dateUnits[0];

      this.chart.dateUnit = dateUnit?.value;
    }
  }

  onTemplateChange() {
    this.template = this.templates.find((tmpl) => tmpl._id === this.chart.templateId);

    if (!this.template) {
      return;
    }
    this.chart.templateId = this.template._id;
    this.isChartSetup = this.chartUtilsService.isChartSetup(this.chart);

    this.onChange.emit(this.chart);
    this.setBreakdownOptions();
  }

  onDateUnitChange() {
    this.onChange.emit(this.chart);
  }

  onTimeFrameDropdownToggle(opened) {
    if (!opened) {
      this.onTimeFrameChange();
    }
  }

  onTimeFrameChange() {
    this.chart.filters = this.chart.filters || {};
    let timeFrame = this.timeFrames.find((tf) => tf.value === this.chart.timeframe);

    if (timeFrame) {
      this.timeFrame = timeFrame;
      timeFrame = timeFrame.range;
    } else {
      this.timeFrame = this.customTimeframe;
      this.chart.timeframe = this.customTimeframe.value;
      timeFrame = range();
    }

    this.dateUnits = this.getDateUnits(timeFrame);
    const dateUnit = this.dateUnits.find((unit) => unit.value === this.chart.dateUnit) || this.dateUnits[0];
    this.chart.dateUnit = dateUnit.value;

    this.onChange.emit(this.chart);
  }

  private getDateUnits(dateRange) {
    const week = range(moment(), moment().add(1, 'week'));
    const month = range(moment(), moment().add(1, 'month'));
    const dateUnits = [ChartDataIntervalOptions[ChartDataIntervals.Day]];

    if (dateRange >= week) {
      dateUnits.push(ChartDataIntervalOptions[ChartDataIntervals.Week]);
    }

    if (dateRange >= month) {
      dateUnits.push(ChartDataIntervalOptions[ChartDataIntervals.Month]);
    }

    return dateUnits;
  }

  onShowAsChange() {
    this.onChange.emit(this.chart);
  }

  onBreakdownChange() {
    this.onChange.emit(this.chart);
  }

  private setBreakdownOptions() {
    this.breakdownOptions = [ChartBreakdownStaticOptions.none, ChartBreakdownStaticOptions.createdBy];

    if (this.template.isOrganisationTemplate) {
      this.breakdownOptions.push(ChartBreakdownStaticOptions.project, ChartBreakdownStaticOptions.team);
    }

    if (this.templatesService.isWorkflow(this.template)) {
      this.breakdownOptions.push(ChartBreakdownStaticOptions.workflowColumns);
    }

    this.template.items.forEach((fields) => {
      // add category items for breakdown
      if (fields.kind === FieldKinds.Category) {
        this.breakdownOptions.push({
          label: fields.description,
          value: fields._id,
        });
      }
    });
  }
}
